import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '../Header/Header'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import { verifyEmail, resendVerificationToken, extractApiMessage } from '../../services/authService'
import './VerifyEmailPage.css'

type Status =
  | 'loading'
  | 'success'
  | 'error'
  | 'resend_form'
  | 'resend_loading'
  | 'resend_success'

const COOLDOWN_SECONDS = 60
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function IconCheck() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M6 16L13 23L26 9" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconClose() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M10 10L22 22M22 10L10 22" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

function IconMail() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="4" y="8" width="24" height="18" rx="3" stroke="#3B82F6" strokeWidth="2" />
      <path d="M4 12L16 19L28 12" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

export default function VerifyEmailPage() {
  const { t } = useTranslation()
  const token = new URLSearchParams(window.location.search).get('token')

  const [status, setStatus] = useState<Status>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [resendError, setResendError] = useState('')
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (!token) {
      setErrorMessage(t('verify_email.invalid_link'))
      setStatus('error')
      return
    }

    verifyEmail(token)
      .then(() => {
        console.log('[INFO] Email verified successfully')
        setStatus('success')
      })
      .catch((err) => {
        console.error('[ERROR] Email verification failed:', err)
        setErrorMessage(extractApiMessage(err, t('verify_email.invalid_link')))
        setStatus('error')
      })
  }, [])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(id)
          return 0
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [cooldown])

  function validateEmail(value: string): boolean {
    if (!value.trim()) {
      setEmailError(t('verify_email.email_required'))
      return false
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError(t('verify_email.email_invalid'))
      return false
    }
    setEmailError('')
    return true
  }

  async function handleResendSubmit() {
    if (!validateEmail(email)) return
    setStatus('resend_loading')
    setResendError('')
    try {
      await resendVerificationToken(email)
      console.log(`[INFO] Verification email resent to: ${email}`)
      setStatus('resend_success')
      setCooldown(COOLDOWN_SECONDS)
    } catch (err) {
      console.error(`[ERROR] Failed to resend verification email to: ${email}`, err)
      setResendError(extractApiMessage(err, t('verify_email.send_failed')))
      setStatus('resend_form')
    }
  }

  return (
    <div className="app">
      <div className="app-container">
        <Header />
        <div className="vep">

          {status === 'loading' && (
            <div className="vep__center">
              <LoadingSpinner size="large" />
              <p className="vep__hint">{t('verify_email.verifying')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="vep__center vep__center--animate">
              <div className="vep__icon vep__icon--success">
                <IconCheck />
              </div>
              <h1 className="vep__title">{t('verify_email.confirmed_title')}</h1>
              <p className="vep__text">{t('verify_email.confirmed_text')}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="vep__center">
              <div className="vep__icon vep__icon--error">
                <IconClose />
              </div>
              <h1 className="vep__title">{t('verify_email.invalid_title')}</h1>
              <p className="vep__text">{errorMessage}</p>
              <button
                className="vep__btn vep__btn--dark"
                onClick={() => setStatus('resend_form')}
              >
                {t('verify_email.resend_email')}
              </button>
            </div>
          )}

          {(status === 'resend_form' || status === 'resend_loading') && (
            <div className="vep__center">
              <div className="vep__icon vep__icon--info">
                <IconMail />
              </div>
              <h1 className="vep__title">{t('verify_email.request_title')}</h1>
              <p className="vep__text">{t('verify_email.request_text')}</p>
              <div className="vep__form">
                <input
                  className={`vep__input${emailError ? ' vep__input--error' : ''}`}
                  type="email"
                  placeholder={t('verify_email.email_placeholder')}
                  value={email}
                  autoFocus
                  disabled={status === 'resend_loading'}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError('')
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleResendSubmit()}
                />
                {emailError && <p className="vep__field-error">{emailError}</p>}
                {resendError && <p className="vep__field-error">{resendError}</p>}
                <button
                  className="vep__btn vep__btn--primary"
                  onClick={handleResendSubmit}
                  disabled={status === 'resend_loading'}
                >
                  {status === 'resend_loading' ? <LoadingSpinner size="small" /> : t('verify_email.send_link')}
                </button>
              </div>
            </div>
          )}

          {status === 'resend_success' && (
            <div className="vep__center">
              <div className="vep__icon vep__icon--info">
                <IconMail />
              </div>
              <h1 className="vep__title">{t('verify_email.sent_title')}</h1>
              <p className="vep__text">{t('verify_email.sent_text')}</p>
              <button
                className="vep__btn vep__btn--outline"
                disabled={cooldown > 0}
                onClick={() => {
                  setResendError('')
                  setStatus('resend_form')
                }}
              >
                {cooldown > 0 ? t('verify_email.resend_countdown', { seconds: cooldown }) : t('verify_email.resend_email')}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
